import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { Button, Checkbox, Dropdown, Input, message, Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import { ApprovalWorkflowItemDto } from '@api/Api';
import { useOrganization } from '@components/OrganizationBoundary';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import {
  ArrowDownIcon,
  CopyIcon,
  DiamondUpgradeIcon,
  EyeClosedIcon,
  EyeOpenedIcon,
  InfoIcon,
  LinkIcon,
  SuccessIcon
} from '@icons';

import './ApprovalSettings.less';

const { TextArea } = Input;

type ApprovalSettingsProps = {
  reviewLink?: string;
  className?: string;
  settings: Settings;
  onUpdate: (settings: Partial<Settings>) => void;
  needSavePassword?: boolean;
  onPasswordSave?: () => Promise<void>;
  disabled?: boolean;
  onBlur?: () => void;
};

type Settings = Pick<
  ApprovalWorkflowItemDto,
  | 'lockDecisions'
  | 'disableNonApprovedDownloads'
  | 'newVersionBehavior'
  | 'password'
  | 'customMessage'
  | 'allowAllVersions'
  | 'allowAllComments'
>;

const newVersionBehaviorOptions: MenuItemType[] = [
  {
    key: 'pause',
    className: 'upload-settings-menu-item',
    title: 'Pause approval routing',
    label: (
      <div className="upload-settings-container">
        <span>Pause approval routing</span>
        <span>Pause until a manager reviews new version.</span>
      </div>
    )
  },
  {
    key: 'restart',
    className: 'upload-settings-menu-item',
    title: 'Restart approval routing',
    label: (
      <div className="upload-settings-container">
        <span>Restart approval routing</span>
        <span>Restart approval routing from the beginning.</span>
      </div>
    )
  },
  {
    key: 'restart_stage',
    className: 'upload-settings-menu-item',
    title: 'Restart current stage',
    label: (
      <div className="upload-settings-container">
        <span>Restart current stage</span>
        <span>Restart current approval routing stage from the beginning.</span>
      </div>
    )
  }
];

function ApprovalSettings({
  reviewLink,
  settings,
  onUpdate,
  needSavePassword,
  onPasswordSave,
  disabled,
  className,
  onBlur
}: ApprovalSettingsProps) {
  const { currentOrganization } = useOrganization();
  const textAreaRef = useRef();

  const upgradePassword =
    !settings.password &&
    !currentOrganization?.entity?.features.passwordProtectedShareLinks;

  const canUpgradeInviteCustomMessage =
    !!currentOrganization?.entity?.features.inviteCustomMessage;

  const canUpgradeLockDecisions =
    settings.lockDecisions ||
    !!currentOrganization?.entity?.features.lockDecisions;

  const canUpgradeDisableNonApprovedDownloads =
    settings.disableNonApprovedDownloads ||
    !!currentOrganization?.entity?.features.disableNonApprovedDownloads;

  const [passwordEnabeled, setPasswordEnabeled] = useState<boolean>(
    !!settings.password
  );
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [password, setPassword] = useState(settings.password);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const [customMessageEnabeled, setCustomMessageEnabeled] = useState<boolean>(
    !!settings.customMessage && canUpgradeInviteCustomMessage
  );
  const [customMessage, setCustomMessage] = useState(
    canUpgradeInviteCustomMessage ? settings.customMessage : ''
  );

  const onPasswordCheckboxChange = (value: boolean) => {
    setPasswordEnabeled(value);
    onUpdate({ password: value ? password : null });
  };

  const onCustomMessageCheckboxChange = (value: boolean) => {
    setCustomMessageEnabeled(value);
    onUpdate({ customMessage: value ? customMessage : null });
  };

  const newVersionBehaviorTitle = newVersionBehaviorOptions.find(
    (item) => item.key === settings.newVersionBehavior
  )?.title;

  return (
    <div className={classNames('approval-settings-container', className)}>
      {!!reviewLink && (
        <div className="link-container">
          <span>Review link</span>
          <div>
            <LinkIcon style={{ marginRight: 8 }} />
            <span id="approval-link">{reviewLink}</span>
            <CopyIcon
              style={{ cursor: 'pointer', marginLeft: 28 }}
              onClick={() => {
                navigator.clipboard.writeText(reviewLink);
                message.success({
                  content: 'Link copied',
                  className: 'message-dark-modal',
                  icon: <SuccessIcon className="anticon" />
                });
              }}
            />
          </div>
        </div>
      )}
      <div className="approval-settings">
        <span className="head-text">Approval routing settings</span>
        <UpgradePlanTooltip
          text=" to a Team plan to set a password."
          placement="topLeft"
          isCanVisible={upgradePassword}
        >
          <Checkbox
            checked={passwordEnabeled}
            onChange={(e) => {
              onPasswordCheckboxChange(e.target.checked);
              if (onBlur) onBlur();
            }}
            disabled={upgradePassword || disabled}
          >
            Add password to review link
            {upgradePassword && (
              <Icon
                className="upgrade-icon"
                component={DiamondUpgradeIcon}
                style={{ marginLeft: '8px' }}
              />
            )}
          </Checkbox>
        </UpgradePlanTooltip>
        {passwordEnabeled && !disabled && (
          <div className="password-review-link-container">
            <Input
              autoFocus
              name="share-link-secret"
              size="large"
              placeholder="Set password"
              type="text"
              autoComplete="off"
              value={password ?? ''}
              onChange={(e) => {
                setPassword(e.target.value);
                onUpdate({ password: e.target.value });
              }}
              onBlur={onBlur}
              style={{
                ['-webkit-text-security' as any]: passwordVisible
                  ? 'none'
                  : 'disc'
              }}
              disabled={disabled}
              suffix={
                passwordVisible ? (
                  <EyeOpenedIcon onClick={() => setPasswordVisible(false)} />
                ) : (
                  <EyeClosedIcon onClick={() => setPasswordVisible(true)} />
                )
              }
            />
            {!!onPasswordSave && (
              <Button
                type="primary"
                onClick={() => {
                  setPasswordLoading(true);
                  onPasswordSave().finally(() => setPasswordLoading(false));
                }}
                disabled={!needSavePassword || disabled}
                loading={passwordLoading}
              >
                Save
              </Button>
            )}
          </div>
        )}
        <UpgradePlanTooltip
          text=" to a Team plan to access this feature."
          placement="topLeft"
          isCanVisible={!canUpgradeInviteCustomMessage}
        >
          <Checkbox
            checked={customMessageEnabeled}
            onChange={(e) => {
              onCustomMessageCheckboxChange(e.target.checked);
              if (onBlur) onBlur();
            }}
            disabled={disabled || !canUpgradeInviteCustomMessage}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Add custom invitation message
              {!canUpgradeInviteCustomMessage && (
                <Icon className="upgrade-icon" component={DiamondUpgradeIcon} />
              )}
            </div>
          </Checkbox>
        </UpgradePlanTooltip>
        {customMessageEnabeled && !disabled && (
          <TextArea
            ref={textAreaRef as any}
            autoFocus
            value={settings.customMessage ?? ''}
            className="custom-invitation-message-textarea"
            placeholder="Send custom message to all reviewers"
            name="customMessage"
            disabled={disabled}
            onChange={(e) => {
              setCustomMessage(e.target.value);
              onUpdate({ customMessage: e.target.value });
            }}
            onBlur={onBlur}
          />
        )}

        <UpgradePlanTooltip
          text=" to a Team plan to access this feature."
          placement="topLeft"
          isCanVisible={!canUpgradeLockDecisions}
        >
          <Checkbox
            checked={settings.lockDecisions}
            onChange={(e) => {
              onUpdate({ lockDecisions: e.target.checked });
              if (onBlur) onBlur();
            }}
            name="lockDecisions"
            disabled={disabled || !canUpgradeLockDecisions}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              Lock decisions
              {canUpgradeLockDecisions && (
                <Tooltip
                  overlayClassName="approval-modal-tooltip"
                  title="Reviewers cannot change their decisions and send new comments after decision is made."
                  trigger="hover"
                  placement="right"
                >
                  <InfoIcon />
                </Tooltip>
              )}
              {!canUpgradeLockDecisions && (
                <Icon
                  className="upgrade-icon"
                  component={DiamondUpgradeIcon}
                  style={{ marginLeft: '3px' }}
                />
              )}
            </div>
          </Checkbox>
        </UpgradePlanTooltip>

        <UpgradePlanTooltip
          text=" to a Team plan to access this feature."
          placement="topLeft"
          isCanVisible={!canUpgradeDisableNonApprovedDownloads}
        >
          <Checkbox
            checked={settings.disableNonApprovedDownloads}
            onChange={(e) => {
              onUpdate({ disableNonApprovedDownloads: e.target.checked });
              if (onBlur) onBlur();
            }}
            name="disableNonApprovedDownloads"
            disabled={disabled || !canUpgradeDisableNonApprovedDownloads}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              Allow download only for approved media
              {canUpgradeDisableNonApprovedDownloads && (
                <Tooltip
                  overlayClassName="approval-modal-tooltip"
                  title="Download option is disabled for media that has not been approved."
                  trigger="hover"
                  placement="right"
                >
                  <InfoIcon />
                </Tooltip>
              )}
              {!canUpgradeDisableNonApprovedDownloads && (
                <Icon
                  className="upgrade-icon"
                  component={DiamondUpgradeIcon}
                  style={{ marginLeft: '3px' }}
                />
              )}
            </div>
          </Checkbox>
        </UpgradePlanTooltip>
        <Checkbox
          checked={settings.allowAllVersions}
          onChange={(e) => {
            onUpdate({ allowAllVersions: e.target.checked });
            if (onBlur) onBlur();
          }}
          name="allowAllVersions"
          disabled={disabled}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            All versions
            <Tooltip
              overlayClassName="approval-modal-tooltip"
              title="Allow reviewers to see all media versions in the approval."
              trigger="hover"
              placement="right"
            >
              <InfoIcon />
            </Tooltip>
          </div>
        </Checkbox>
        <Checkbox
          checked={settings.allowAllComments}
          onChange={(e) => {
            onUpdate({ allowAllComments: e.target.checked });
            if (onBlur) onBlur();
          }}
          name="allowAllComments"
          disabled={disabled}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            Show existing comments
            <Tooltip
              overlayClassName="approval-modal-tooltip"
              title="When disabled, all existing comments will be hidden on this approval link."
              trigger="hover"
              placement="right"
            >
              <InfoIcon />
            </Tooltip>
          </div>
        </Checkbox>
      </div>
      <div className="upload-settings">
        <span className="head-text">Upload settings</span>
        <span className="upload-settings-description">
          How would you like to proceed with routing approvals when a new media
          version is uploaded by your team?
        </span>
        <Dropdown
          menu={{
            style: { padding: '12px', width: 428 },
            selectedKeys: [settings.newVersionBehavior],
            selectable: true,
            onClick: ({ key }) => {
              onUpdate({ newVersionBehavior: key as any });
              if (onBlur) onBlur();
            },
            items: newVersionBehaviorOptions
          }}
          placement="topLeft"
          trigger={['click']}
          disabled={disabled}
        >
          <div className="preference-select">
            <span>{newVersionBehaviorTitle}</span>
            <ArrowDownIcon />
          </div>
        </Dropdown>
      </div>
    </div>
  );
}

export default ApprovalSettings;
